<template>
  <base-dialog
    id="dialogFixed"
    height="100vh"
    width="100vw"
    data-testid="statechartWizard.dialog"
    :title="compProps.type === 'examples' ? 'Examples' : 'My Statecharts'"
    :cancelable="cancelable"
    dialog-id="statechartWizard"
    @complete-dialog="loadModel"
    @cancel-dialog="cancel"
  >
    <template #subHeader>
      <div class="d-flex align-center" style="width: 100%; position: relative">
        <v-text-field
          v-model="search"
          style="width: 350px; flex: none"
          density="compact"
          prepend-inner-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          :disabled="statecharts.length < 1 && statechartsExample.length < 1"
        ></v-text-field>
        <div
          v-if="compProps.type === 'statecharts'"
          class="d-flex align-center"
          style="position: absolute; left: 50%; transform: translateX(-50%); gap: 8px"
        >
          <span class="text-body-2">{{ selectedCount }} selected</span>
          <v-btn color="error" variant="elevated" prepend-icon="mdi-delete-forever" :disabled="selectedCount === 0" @click="cardsGroup?.initBatchDelete()">Delete</v-btn>
          <v-btn color="primary" variant="outlined" prepend-icon="mdi-download" :disabled="selectedCount === 0" @click="cardsGroup?.downloadSelected()">Download</v-btn>
          <v-btn color="primary" variant="outlined" prepend-icon="mdi-checkbox-multiple-marked" :disabled="filteredStatecharts.length === 0" @click="cardsGroup?.selectAll()">Select all</v-btn>
          <v-btn color="primary" variant="outlined" prepend-icon="mdi-checkbox-multiple-blank-outline" :disabled="selectedCount === 0" @click="cardsGroup?.clearSelection()">Deselect all</v-btn>
        </div>
        <div class="d-flex align-center" style="margin-left: auto; gap: 4px">
          <v-select
            v-model="sortField"
            :items="sortFieldOptions"
            density="compact"
            hide-details
            style="width: 180px"
            :disabled="statecharts.length < 1 && statechartsExample.length < 1"
          ></v-select>
          <v-btn
            icon
            variant="text"
            density="compact"
            :disabled="statecharts.length < 1 && statechartsExample.length < 1"
            @click="sortDir = sortDir === 'asc' ? 'desc' : 'asc'"
          >
            <v-icon>{{ sortDir === "asc" ? "mdi-sort-ascending" : "mdi-sort-descending" }}</v-icon>
          </v-btn>
        </div>
      </div>
    </template>
    <template #content>
      <statechart-cards-group
        v-if="compProps.type === 'statecharts'"
        ref="cardsGroup"
        :with-new="true"
        :statecharts="filteredStatecharts"
        @update:reload-models="reload"
        @openStatechart="loadModel"
        @selectionChanged="selectedCount = $event"
      ></statechart-cards-group>
      <statechart-cards-group
        v-else-if="compProps.type === 'examples' && filteredStatechartsExample.length > 0"
        :with-new="false"
        :statecharts="filteredStatechartsExample"
        @openStatechart="loadModel"
      ></statechart-cards-group>
      <div v-else-if="!loading" class="d-flex justify-center align-center" style="height: 100%; opacity: 0.5">
        - no {{ search.length > 0 ? "matching" : "" }}
        examples available -
      </div>
    </template>
    <template #footer>
      <v-btn-dialog-action v-if="compProps.cancelable" data-testid="statechartWizard.cancel-button" @click="cancel"
        >Cancel</v-btn-dialog-action
      >
    </template>
  </base-dialog>
</template>

<script setup lang="ts">
import BaseDialog from "../../shared/BaseDialog.vue";
import { MessageEventBus, ProgressBarEventBus, WizardEventBus } from "../../event-bus";
import { StatechartsService } from "../services/statecharts/statecharts-service";
import StatechartCardsGroup from "./StatechartCardsGroup.vue";
import { ref, inject, watch, customRef } from "vue";

function useDebouncedRef<T>(value: T, delay = 300) {
  let timeout: ReturnType<typeof setTimeout>;
  return customRef<T>((track, trigger) => ({
    get() {
      track();
      return value;
    },
    set(newValue) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        value = newValue;
        trigger();
      }, delay);
    },
  }));
}
import { NewStatechart } from "./utils/NewStatechart";
import RecentItemsService, { RecentFile } from "../services/storage/recent-items-service";

const emit = defineEmits(["cancel", "statechartSelected"]);
const compProps = defineProps<{
  type: "examples" | "statecharts";
  cancelable: boolean;
}>();

const statecharts = ref<RecentFile[]>([]);
const filteredStatecharts = ref<RecentFile[]>([]);

const statechartsExample = ref<RecentFile[]>([]);
const filteredStatechartsExample = ref<RecentFile[]>([]);

const loading = ref(true);
const search = useDebouncedRef("");
const selectedCount = ref(0);
const cardsGroup = ref<{ initBatchDelete: () => void; downloadSelected: () => void; clearSelection: () => void; selectAll: () => void } | null>(null);

type SortField = "title" | "createdAt" | "modifiedAt";
type SortDir = "asc" | "desc";

const sortFieldOptions: { title: string; value: SortField }[] = [
  { title: "Title", value: "title" },
  { title: "Creation Date", value: "createdAt" },
  { title: "Modified Date", value: "modifiedAt" },
];
const sortField = ref<SortField>("modifiedAt");
const sortDir = ref<SortDir>("desc");

const exampleStatechartService: StatechartsService = inject<StatechartsService>("exampleStatechartService");

const cloudStatechartService: StatechartsService = inject<StatechartsService>("cloudStatechartService");

const recentItemsService: RecentItemsService = inject<RecentItemsService>("recentItemsService");

reload();

watch([search, sortField, sortDir], ([search]) => {
  applyFiltersAndSort(search as string);
});

function applyFiltersAndSort(searchTerm: string) {
  const searchLowerCase = searchTerm.toLowerCase();
  const filtered = filterStatecharts(searchLowerCase, statecharts.value);
  const filteredExample = filterStatecharts(searchLowerCase, statechartsExample.value);

  if (filtered.length > 0) {
    filteredStatecharts.value = sortStatecharts(filtered);
  } else {
    filteredStatecharts.value = [];
    if (searchTerm.length > 0) {
      MessageEventBus.emit("information", {
        text: "No matching " + compProps.type + " available.",
        id: "statechart-wizard-search",
      });
    }
  }

  filteredStatechartsExample.value = filteredExample.length > 0 ? sortStatecharts(filteredExample) : [];
}

function filterStatecharts(searchLowerCase: string, statecharts: RecentFile[]) {
  return statecharts.filter((el) => {
    return (
      el.model.title?.toLowerCase().includes(searchLowerCase) ||
      el.model.description?.toLowerCase().includes(searchLowerCase)
    );
  });
}

function sortStatecharts(list: RecentFile[]): RecentFile[] {
  return [...list].sort((a, b) => {
    const field = sortField.value;
    let aVal: string | number;
    let bVal: string | number;
    if (field === "title") {
      aVal = a.model.title?.toLowerCase() ?? "";
      bVal = b.model.title?.toLowerCase() ?? "";
    } else {
      aVal = a.model[field]?.getTime() ?? 0;
      bVal = b.model[field]?.getTime() ?? 0;
    }
    if (aVal < bVal) return sortDir.value === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDir.value === "asc" ? 1 : -1;
    return 0;
  });
}

function cancel() {
  emit("cancel");
}

async function reload() {
  ProgressBarEventBus.emit("startTask");
  loading.value = true;
  if (compProps.type === "examples") {
    await exampleStatechartService
      .getModels(true, false)
      .then((res) => {
        const exampleFilesList: RecentFile[] = [];
        for (const el of res) {
          exampleFilesList.push({
            modelId: el.id,
            model: el,
            lastModified: el.modifiedAt,
          });
        }
        init(exampleFilesList, "examples");
        cleanUp();
      })
      .catch(() => {
        cleanUp();
        MessageEventBus.emit("error", {
          text: "Failed to load examples.",
          id: "statechart-wizard-fetch-statecharts",
        });
        emit("cancel");
      });
  } else {
    recentItemsService
      .getRecentFilesList(true, true)
      .then((res) => {
        init(res.all, "statecharts");
        cleanUp();
      })
      .catch(() => {
        cleanUp();
        MessageEventBus.emit("error", {
          text: "Failed to load statecharts.",
          id: "statechart-wizard-fetch-statecharts",
        });
        emit("cancel");
      });
  }
}

function cleanUp() {
  ProgressBarEventBus.emit("finishedTask");
  loading.value = false;
}

function init(res: RecentFile[], type: "examples" | "statecharts") {
  if (type === "examples") {
    statechartsExample.value = res;
    filteredStatechartsExample.value = sortStatecharts(res);
  } else {
    statecharts.value = res;
    filteredStatecharts.value = sortStatecharts(res);
  }
  if (res.length < 1 && type === "statecharts") {
    MessageEventBus.emit("information", {
      text: "No statecharts available.",
      id: "statechart-wizard-fetch-statecharts",
    });
  }
  search.value = "";
  WizardEventBus.emit("initialised");
}

async function loadModel(stc: RecentFile | NewStatechart) {
  if (compProps.type === "statecharts") {
    ProgressBarEventBus.emit("startTask");
    // new statechart selected
    if (!stc || !("model" in stc)) {
      load(null);
    }
    // one of "my statecharts" is selected
    else {
      await cloudStatechartService
        .getModel(stc.modelId)
        .then((res) => load(res, stc))
        .catch(failedToLoad);
    }
  } else {
    if (stc) {
      ProgressBarEventBus.emit("startTask");
      await exampleStatechartService.getModel(stc.modelId).then(load).catch(failedToLoad);
    }
  }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function load(res: any, stc?: RecentFile) {
  ProgressBarEventBus.emit("finishedTask");
  emit("statechartSelected", res, stc);
}

function failedToLoad(err) {
  ProgressBarEventBus.emit("finishedTask");
  MessageEventBus.emit("error", {
    text: "Failed to load statechart.",
    id: "statechart-wizard-fetch-statechart",
  });
}
</script>
